import { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import ScrollReveal from '../components/ScrollReveal';

var agents = [
  {
    id: 'marketing',
    label: 'Marketing Agent',
    title: 'Automate your\nentire funnel.',
    desc: 'Writes copy, plans campaigns, analyzes performance, and optimizes every stage — so you can focus on strategy, not execution.',
    status: 'live',
    path: '/products/marketing-agent',
    cta: 'Read more',
    color: '#d46a28',
    colorLight: 'rgba(212, 106, 40, 0.06)',
  },
  {
    id: 'devdebug',
    title: 'Dev Debug Agent',
    desc: 'An AI pair-programmer that helps you debug faster, write cleaner code, and ship with confidence. From stack traces to solutions in seconds.',
    status: 'soon',
    path: null,
    cta: 'Coming Soon',
    color: '#6366f1',
    colorLight: 'rgba(99, 102, 241, 0.08)',
  },
  {
    id: 'learning',
    title: 'Learning Agent',
    desc: 'A personalized AI tutor that adapts to your pace, builds custom learning paths, and helps you master any technical topic.',
    status: 'soon',
    path: null,
    cta: 'Coming Soon',
    color: '#0ea5e9',
    colorLight: 'rgba(14, 165, 233, 0.08)',
  },
];

function AgentIllustration({ id, color }) {
  var ref = useRef(null);
  var [visible, setVisible] = useState(false);

  useEffect(function () {
    var el = ref.current;
    if (!el) return;
    var observer = new IntersectionObserver(
      function (entries) {
        if (entries[0].isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return function () { observer.disconnect(); };
  }, []);

  var animClass = 'pstack-illust' + (visible ? ' pstack-illust--visible' : '');

  if (id === 'marketing') {
    var cells = [
      { x: 0, y: 0, w: 2, label: 'SEO', o: 0.85 },
      { x: 2, y: 0, w: 1, label: 'Ads', o: 0.55 },
      { x: 3, y: 0, w: 1, label: 'Social', o: 0.7 },
      { x: 4, y: 0, w: 2, label: 'Email', o: 0.6 },
      { x: 0, y: 1, w: 1, label: 'Blog', o: 0.45 },
      { x: 1, y: 1, w: 1, label: 'Video', o: 0.75 },
      { x: 2, y: 1, w: 2, label: 'Landing', o: 0.9 },
      { x: 4, y: 1, w: 1, label: 'CPC', o: 0.5 },
      { x: 5, y: 1, w: 1, label: 'Refer', o: 0.35 },
      { x: 0, y: 2, w: 1, label: 'CTR', o: 0.65 },
      { x: 1, y: 2, w: 2, label: 'Conversion', o: 0.95 },
      { x: 3, y: 2, w: 1, label: 'ROI', o: 0.8 },
      { x: 4, y: 2, w: 2, label: 'Retention', o: 0.55 },
      { x: 0, y: 3, w: 2, label: 'Revenue', o: 0.9 },
      { x: 2, y: 3, w: 1, label: 'LTV', o: 0.7 },
      { x: 3, y: 3, w: 2, label: 'Growth', o: 0.6 },
      { x: 5, y: 3, w: 1, label: 'NPS', o: 0.4 },
    ];
    var cellW = 44, cellH = 38, gap = 4, ox = 12, oy = 16;
    return (
      <svg ref={ref} viewBox="0 0 310 200" fill="none" className={animClass}>
        {cells.map(function (c, i) {
          var rx = ox + c.x * (cellW + gap);
          var ry = oy + c.y * (cellH + gap);
          var rw = c.w * cellW + (c.w - 1) * gap;
          return (
            <g key={i} className="pstack-anim-child" style={{ '--anim-i': i }}>
              <rect x={rx} y={ry} width={rw} height={cellH} rx="6" fill={color} opacity={c.o * 0.85} />
              <text
                x={rx + rw / 2} y={ry + cellH / 2 + 1}
                textAnchor="middle" dominantBaseline="central"
                fill="#fff" fontSize="9.5" fontWeight="700"
                letterSpacing="0.02em" opacity={0.92}
              >{c.label}</text>
            </g>
          );
        })}
      </svg>
    );
  }
  if (id === 'devdebug') {
    return (
      <svg ref={ref} viewBox="0 0 320 220" fill="none" className={animClass}>
        <g className="pstack-anim-child" style={{ '--anim-i': 0 }}>
          <rect x="20" y="16" width="280" height="188" rx="12" fill="#1e1e1e" stroke={color} strokeWidth="1" opacity="0.9" />
          <rect x="20" y="16" width="280" height="32" rx="12" fill="#2d2d2d" />
          <circle cx="40" cy="32" r="5" fill="#ff5f57" />
          <circle cx="56" cy="32" r="5" fill="#febc2e" />
          <circle cx="72" cy="32" r="5" fill="#28c840" />
        </g>
        <g className="pstack-anim-child" style={{ '--anim-i': 1 }}>
          <rect x="40" y="60" width="16" height="5" rx="2" fill="#569cd6" opacity="0.8" />
          <rect x="62" y="60" width="60" height="5" rx="2" fill="#9cdcfe" opacity="0.6" />
          <rect x="128" y="60" width="30" height="5" rx="2" fill="#ce9178" opacity="0.6" />
        </g>
        <g className="pstack-anim-child" style={{ '--anim-i': 2 }}>
          <rect x="40" y="74" width="24" height="5" rx="2" fill="#c586c0" opacity="0.7" />
          <rect x="70" y="74" width="45" height="5" rx="2" fill="#dcdcaa" opacity="0.6" />
        </g>
        <g className="pstack-anim-child" style={{ '--anim-i': 3 }}>
          <rect x="36" y="102" width="248" height="22" rx="4" fill="#ff000015" stroke="#ff5f57" strokeWidth="1" />
          <rect x="44" y="109" width="14" height="5" rx="2" fill="#ff5f57" opacity="0.9" />
          <rect x="64" y="109" width="90" height="5" rx="2" fill="#f44747" opacity="0.7" />
        </g>
        <g className="pstack-anim-child" style={{ '--anim-i': 4 }}>
          <rect x="36" y="134" width="248" height="46" rx="6" fill={color} opacity="0.15" stroke={color} strokeWidth="1" />
          <rect x="48" y="144" width="10" height="5" rx="2" fill={color} opacity="0.8" />
          <rect x="64" y="144" width="50" height="5" rx="2" fill={color} opacity="0.5" />
          <rect x="48" y="158" width="16" height="5" rx="2" fill="#28c840" opacity="0.8" />
          <rect x="70" y="158" width="80" height="5" rx="2" fill="#d4d4d4" opacity="0.5" />
        </g>
      </svg>
    );
  }
  return (
    <svg ref={ref} viewBox="0 0 320 220" fill="none" className={animClass}>
      <g className="pstack-anim-child" style={{ '--anim-i': 0 }}>
        <rect x="20" y="16" width="280" height="188" rx="12" fill="#fff" stroke={color} strokeWidth="1.5" opacity="0.15" />
      </g>
      <g className="pstack-anim-child" style={{ '--anim-i': 1 }}>
        <circle cx="160" cy="90" r="40" stroke={color} strokeWidth="6" opacity="0.12" />
        <path d="M160 50 A40 40 0 1 1 120.2 90" stroke={color} strokeWidth="6" strokeLinecap="round" opacity="0.7" />
        <text x="160" y="96" textAnchor="middle" fill={color} fontSize="18" fontWeight="800" opacity="0.8">75%</text>
      </g>
      <g className="pstack-anim-child" style={{ '--anim-i': 2 }}>
        <rect x="48" y="148" width="224" height="16" rx="4" fill={color} opacity="0.06" />
        <rect x="52" y="152" width="140" height="8" rx="3" fill={color} opacity="0.5" />
        <circle cx="256" cy="156" r="6" fill={color} opacity="0.6" />
        <polyline points="253,156 255,158.5 259,153.5" stroke="#fff" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </g>
      <g className="pstack-anim-child" style={{ '--anim-i': 3 }}>
        <rect x="48" y="172" width="224" height="16" rx="4" fill={color} opacity="0.06" />
        <rect x="52" y="176" width="100" height="8" rx="3" fill={color} opacity="0.35" />
        <circle cx="256" cy="180" r="6" stroke={color} strokeWidth="1.5" fill="none" opacity="0.3" />
      </g>
      <g className="pstack-anim-child" style={{ '--anim-i': 4 }}>
        <rect x="48" y="30" width="60" height="20" rx="10" fill={color} opacity="0.1" />
        <rect x="56" y="37" width="44" height="6" rx="3" fill={color} opacity="0.4" />
        <rect x="118" y="30" width="50" height="20" rx="10" fill={color} opacity="0.07" />
        <rect x="126" y="37" width="34" height="6" rx="3" fill={color} opacity="0.3" />
      </g>
    </svg>
  );
}

function Products() {
  return (
    <div className="page-home">
      <SEO
        title="Anthroper Products — AI Agents for Real Work"
        description="Purpose-built AI agents for marketing, development, and learning. One platform, real results."
        path="/products"
      />

      <section className="home-hero" style={{ paddingTop: 'var(--space-7)', paddingBottom: 'var(--space-4)' }}>
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <ScrollReveal>
            <p className="home-hero-badge">One platform. Purpose-built AI agents.</p>
          </ScrollReveal>
          <ScrollReveal delay={100}>
            <h1 className="home-hero-title">
              AI Agents for<br />
              <span className="home-hero-accent">Real Work</span>
            </h1>
          </ScrollReveal>
          <ScrollReveal delay={200}>
            <p className="home-hero-sub">
              Not chatbots. Not copilots. Agents that actually do the work — writing, debugging, teaching — so you can focus on what matters.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Stacking cards — pure CSS grid + sticky */}
      <div className="container">
        <ul className="pstack-grid">
          {agents.map(function (agent, i) {
            var isLive = agent.status === 'live';
            return (
              <li key={agent.id} className="pstack-item" style={{ '--pstack-i': i }}>
                <div className="pstack-card" style={{ '--pstack-color': agent.color, '--pstack-color-light': agent.colorLight }}>
                  <div className="pstack-card-inner">
                    <div className="pstack-text">
                      <div className="pstack-header">
                        {agent.label ? (
                          <span className="pstack-label">{agent.label}</span>
                        ) : null}
                        {isLive ? (
                          <span className="products-status-live">Available Now</span>
                        ) : (
                          <span className="products-status-soon">Coming Soon</span>
                        )}
                      </div>
                      <h3 className="pstack-title">
                        {agent.title.split('\n').map(function (line, li) {
                          return li === 0 ? line : <span key={li}><br />{line}</span>;
                        })}
                      </h3>
                      <p className="pstack-desc">{agent.desc}</p>
                      {isLive ? (
                        <Link to={agent.path} className="pstack-cta">{agent.cta} →</Link>
                      ) : (
                        <span className="pstack-cta pstack-cta--disabled">{agent.cta}</span>
                      )}
                    </div>
                    <div className="pstack-visual">
                      <AgentIllustration id={agent.id} color={agent.color} />
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <section className="home-section home-section--tinted">
        <div className="container">
          <ScrollReveal>
            <h2 className="home-section-title" style={{ textAlign: 'center' }}>What's Coming</h2>
            <p className="home-section-sub" style={{ textAlign: 'center', margin: '0 auto var(--space-5)' }}>We're building a suite of agents — each purpose-built for a specific domain.</p>
          </ScrollReveal>
          <div className="products-coming-timeline">
            <ScrollReveal delay={60}>
              <div className="products-timeline-item">
                <div className="products-timeline-dot" style={{ background: '#6366f1' }}></div>
                <div className="products-timeline-content">
                  <h3>Dev Debug Agent</h3>
                  <p>Instant stack trace analysis, code review with best-practice recommendations, automated test generation, and performance profiling.</p>
                </div>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={120}>
              <div className="products-timeline-item">
                <div className="products-timeline-dot" style={{ background: '#0ea5e9' }}></div>
                <div className="products-timeline-content">
                  <h3>Learning Agent</h3>
                  <p>Adaptive learning paths, interactive exercises with real-time feedback, concept explanations with diagrams, and spaced repetition.</p>
                </div>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={180}>
              <div className="products-timeline-item">
                <div className="products-timeline-dot" style={{ background: 'var(--text-subtle)' }}></div>
                <div className="products-timeline-content">
                  <h3>And more...</h3>
                  <p>We're exploring agents for sales, customer support, and data analysis. Want early access? Get in touch.</p>
                </div>
              </div>
            </ScrollReveal>
          </div>
          <ScrollReveal delay={240}>
            <div style={{ textAlign: 'center', marginTop: 'var(--space-6)' }}>
              <Link to="/contact" className="btn btn-primary btn-lg">Get Early Access</Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="home-section">
        <div className="container">
          <ScrollReveal>
            <div className="home-cta-banner">
              <div className="home-cta-text">
                <h2>Start with the Marketing Agent</h2>
                <p>Available now. 14-day free trial, no credit card required.</p>
              </div>
              <div className="home-cta-actions">
                <Link to="/products/marketing-agent" className="btn btn-primary btn-lg">Explore Marketing Agent</Link>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}

export default Products;
